import { useState } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { FileOutput, Loader2 } from 'lucide-react';
import { Transcription } from '@/types/transcription';
import { useProjects } from '@/hooks/useProjects';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface TranscriptionExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transcription: Transcription;
}

export const TranscriptionExportModal = ({
  open,
  onOpenChange,
  transcription,
}: TranscriptionExportModalProps) => {
  const { projects, loading: projectsLoading } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  const displayTitle = transcription.title || transcription.filename;

  // Build the note body content
  const buildNoteBody = () => {
    const lines: string[] = [];
    
    if (transcription.conversation_date) {
      lines.push(`**Gespreksdatum:** ${format(new Date(transcription.conversation_date), 'd MMMM yyyy', { locale: nl })}`);
    }
    
    if (transcription.extra_info) {
      lines.push(`**Extra informatie:** ${transcription.extra_info}`);
    }
    
    lines.push('');
    lines.push('## Samenvatting');
    lines.push(transcription.summary || 'Geen samenvatting beschikbaar');
    
    if (transcription.action_items && transcription.action_items.length > 0) {
      lines.push('');
      lines.push('## Actiepunten');
      transcription.action_items.forEach((item) => {
        const priority = item.priority === 'high' ? '🔴' : item.priority === 'medium' ? '🟡' : '🟢';
        const speaker = item.speaker ? ` (${item.speaker})` : '';
        lines.push(`- ${priority} ${item.task} - ${item.deadline}${speaker}`);
      });
    }
    
    return lines.join('\n');
  };

  const handleExport = async () => {
    if (!selectedProjectId) {
      toast({
        title: 'Selecteer een project',
        description: 'Kies eerst een project om naar te exporteren.',
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);
    
    try {
      const { error } = await supabase.from('project_notes').insert({
        project_id: selectedProjectId,
        title: displayTitle,
        body: buildNoteBody(),
        note_type: 'meeting_notes',
      });

      if (error) throw error;

      toast({
        title: 'Geëxporteerd',
        description: 'De transcriptie is opgeslagen als notule in het project.',
      });
      
      onOpenChange(false);
    } catch (error: any) {
      console.error('Export failed:', error);
      toast({
        title: 'Export mislukt',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const previewContent = buildNoteBody();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileOutput className="h-5 w-5" />
            Opslaan naar Project
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Project</Label>
            <Select
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
              disabled={projectsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={projectsLoading ? 'Laden...' : 'Selecteer een project'} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Preview</Label>
            <div className="border rounded-lg p-3 bg-muted/30 mt-1">
              <div className="text-sm font-medium mb-2">Titel: {displayTitle}</div>
              <div className="text-xs text-muted-foreground mb-1">Type: Notule gesprek</div>
              <ScrollArea className="h-[200px]">
                <div className="text-sm whitespace-pre-wrap">{previewContent}</div>
              </ScrollArea>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuleren
          </Button>
          <Button onClick={handleExport} disabled={isExporting || !selectedProjectId}>
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporteren...
              </>
            ) : (
              <>
                <FileOutput className="h-4 w-4 mr-2" />
                Opslaan
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
